// Localização: frontend/src/components/AgendaTarefas/AgendaTarefaForm.jsx (versão 0.1.8)

import React, { useState, useEffect, useMemo } from "react";
import { Clock, XCircle, Save, Activity, Droplets, RotateCcw, UtensilsCrossed, Dumbbell, Pill, FileText, User, ChevronDown, Scale, Droplet } from "lucide-react"; 

// Importando o Modal.js genérico e refatorado
import Modal from "../UI/Modal"; 
import DropdownMenu from "../UI/DropdownMenu";
import { usePacienteList } from "../../hooks/usePacienteList";
import { useAgendaTarefas } from "../../hooks/useAgendaTarefas"; 
// 🛑 REMOVEMOS A IMPORTAÇÃO: O ConfirmacaoModal será gerenciado pelo componente pai
// import ConfirmacaoModal from "../Common/ConfirmacaoModal";
import Alert from "../UI/Alert";
import { formatDateTimeLocalToPostgres, parseLocalDateTime } from "../../utils/formatters";
import { getPacienteFotoUrl } from "../../utils/imageUtils";

// Mapeamento dos tipos de tarefa para o backend com ícones do Lucide
const TIPO_TAREFAS = [
    { value: "Sinais Vitais", label: "Sinais Vitais", icon: Activity },
    { value: "Higienização", label: "Higienização", icon: Droplets },
    { value: "Mudança de Decúbito", label: "Mudança de Decúbito", icon: RotateCcw },
    { value: "Alimentação", label: "Alimentação", icon: UtensilsCrossed },
    { value: "Atividade Física", label: "Atividade Física", icon: Dumbbell },
    { value: "Medicação", label: "Medicação", icon: Pill },
    { value: "Pesagem", label: "Pesagem", icon: Scale },
    { value: "Glicemia", label: "Glicemia", icon: Droplet },
    { value: "Outro", label: "Outro", icon: FileText },
];

// Função auxiliar para formatar data para datetime-local
// CORREÇÃO: Usa parseLocalDateTime para evitar problemas de timezone
const formatToDateTimeLocal = (dateString) => {
    if (!dateString) return "";
    
    // Usa parseLocalDateTime para interpretar como horário local
    const date = parseLocalDateTime(dateString);
    if (!date) return "";
    
    // Formata para datetime-local (YYYY-MM-DDTHH:mm)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};


/**
 * Formulário para criação e edição de tarefas SIMPLES (não recorrentes).
 * @param {boolean} isOpen - Controla a visibilidade (necessário pelo componente Modal.js)
 * @param {function} onClose - Função para fechar o modal (necessário pelo componente Modal.js)
 * @param {function} onSucesso - Callback de sucesso
 * @param {object | null} tarefaParaEditar - Dados da tarefa se for modo edição
 * @param {function} onOpenConfirmacao - NOVO PROP: Função para solicitar que o pai abra o modal de confirmação.
 */
const AgendaTarefaForm = ({ 
    isOpen, 
    onClose, 
    onSucesso, 
    tarefaParaEditar, 
    onOpenConfirmacao 
}) => {
    const isEditing = !!tarefaParaEditar;

    const { pacientes, isLoading: isLoadingPacientes } = usePacienteList();
    const { createTarefaSimples, updateTarefa } = useAgendaTarefas(); 

    // Usando useMemo para garantir que initialFormData só seja recriado se for realmente necessário
    const initialFormData = useMemo(() => ({
        paciente_id: "",
        tipo_tarefa: TIPO_TAREFAS[0].value, // Padrão: Sinais Vitais
        hora_agendada: "", 
        descricao: "",
        data_fim: "", 
        permanente: false,
    }), []);

    const [formData, setFormData] = useState(initialFormData);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [alerta, setAlerta] = useState(null);
    // 🛑 REMOVEMOS O ESTADO: A confirmação é gerenciada externamente
    // const [showConfirmModal, setShowConfirmModal] = useState(false); 

    // Efeito para carregar dados da tarefa no modo de edição/limpar na criação
    useEffect(() => {
        if (isEditing && tarefaParaEditar) {
            setFormData({
                paciente_id: tarefaParaEditar.paciente_id || "",
                tipo_tarefa: tarefaParaEditar.tipo_tarefa || TIPO_TAREFAS[0].value, // Padrão: Sinais Vitais
                hora_agendada: formatToDateTimeLocal(tarefaParaEditar.hora_agendada), 
                descricao: tarefaParaEditar.descricao || "",
                data_fim: "", 
                permanente: false,
            });
        } else {
            setFormData(initialFormData);
        }
        // Limpa alertas ao abrir/mudar o modo
        setAlerta(null);
    }, [isEditing, tarefaParaEditar, initialFormData]); 

    // Lógica para fechar o modal
    const handleCancel = () => {
        onClose(); 
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    /**
     * O núcleo do submit agora pede confirmação ao componente pai,
     * passando a função de envio para ser executada após a confirmação.
     */
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.paciente_id || !formData.hora_agendada) {
            setAlerta({ tipo: "error", mensagem: "Paciente e Horário Agendado são obrigatórios." });
            return;
        }
        setAlerta(null);

        // 🛑 NOVO FLUXO: Envia os dados necessários e a função de envio para o componente pai.
        if (onOpenConfirmacao) {
            // Garantir que paciente_id seja convertido para número se necessário
            const pacienteIdFormatado = formData.paciente_id ? Number(formData.paciente_id) || formData.paciente_id : null;
            
            console.log('[AgendaTarefaForm] Enviando dados para confirmação:', {
                paciente_id: formData.paciente_id,
                paciente_id_formatado: pacienteIdFormatado,
                tipo_original: typeof formData.paciente_id,
                hora_agendada: formData.hora_agendada
            });
            
            onOpenConfirmacao({
                isEditing,
                horaAgendada: formData.hora_agendada,
                paciente_id: pacienteIdFormatado,
                onConfirm: handleConfirmarEnvio,
            });
        } else {
            // Caso o componente pai não passe a prop de confirmação, envia direto
            handleConfirmarEnvio();
        }
    };

    const handleConfirmarEnvio = async () => {
        // 🛑 REMOVEMOS: setShowConfirmModal(false) - pois o modal é externo.
        
        setIsSubmitting(true);
        setAlerta(null);

        try {
            // CORREÇÃO CRÍTICA: Converte datetime-local para formato PostgreSQL sem conversão de timezone
            // O formato datetime-local é "YYYY-MM-DDTHH:mm" (horário local)
            // Precisamos enviar como "YYYY-MM-DD HH:mm:ss" (formato PostgreSQL) preservando o horário local
            const horaAgendadaFormatada = formatDateTimeLocalToPostgres(formData.hora_agendada);
            
            if (!horaAgendadaFormatada) {
                setAlerta({
                    tipo: "error",
                    mensagem: "Erro ao formatar a data/hora. Por favor, verifique o horário agendado.",
                });
                setIsSubmitting(false);
                return;
            }
            
            const payload = {
                paciente_id: formData.paciente_id,
                tipo_tarefa: formData.tipo_tarefa,
                // Envia no formato PostgreSQL (YYYY-MM-DD HH:mm:ss) sem conversão de timezone
                hora_agendada: horaAgendadaFormatada,
                descricao: formData.descricao || null,
                data_fim: null, 
            };

            let response;
            if (isEditing) {
                // Se estiver editando, usa o ID da tarefaParaEditar
                response = await updateTarefa(tarefaParaEditar.id, payload);
            } else {
                response = await createTarefaSimples(payload);
            }

            setAlerta({ 
                tipo: "success", 
                mensagem: response.message || (isEditing ? "Tarefa atualizada com sucesso!" : "Tarefa criada com sucesso!") 
            });
            // 🚨 Chamada de sucesso deve fechar o modal e/ou atualizar a lista
            if (onSucesso) onSucesso();
            
        } catch (error) {
            console.error(isEditing ? "Erro ao editar tarefa:" : "Erro ao criar tarefa simples:", error);
            setAlerta({
                tipo: "error",
                mensagem: error.message || error.response?.data?.message || `Falha ao ${isEditing ? 'atualizar' : 'criar'} a tarefa.`,
            });
        } finally {
            setIsSubmitting(false);
        }
    };


    return (
        <Modal 
            isOpen={isOpen} 
            onClose={handleCancel} 
            title={isEditing ? `✏️ Editar Tarefa ${tarefaParaEditar?.id}` : "✨ Agendar Tarefa Única"}
            size="md" 
        >
            {/* Conteúdo do Formulário */}
            {alerta && <div className="mb-4"><Alert type={alerta.tipo} message={alerta.mensagem} /></div>}

            <form onSubmit={handleSubmit} className="space-y-5">
                
                {/* Paciente - Usando DropdownMenu */}
                <div>
                    <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                        <User size={16} className="text-gray-500" />
                        Paciente
                    </label>
                    <DropdownMenu
                        trigger={
                            <button
                                type="button"
                                onClick={(e) => e.preventDefault()}
                                disabled={isEditing}
                                className={`w-full flex items-center justify-between gap-2 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 hover:bg-gray-50 transition ${
                                    isEditing ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    {(() => {
                                        const pacienteSelecionado = pacientes?.find(p => String(p.id) === String(formData.paciente_id));
                                        const fotoUrl = pacienteSelecionado?.fotoURL ? getPacienteFotoUrl(pacienteSelecionado.fotoURL) : null;
                                        return (
                                            <>
                                                <div className="relative h-6 w-6 flex-shrink-0">
                                                    {fotoUrl ? (
                                                        <img 
                                                            src={fotoUrl} 
                                                            alt={pacienteSelecionado?.nome_completo || 'Paciente'} 
                                                            className="h-6 w-6 rounded-full object-cover border border-gray-200"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                if (e.target.nextSibling) {
                                                                    e.target.nextSibling.style.display = 'flex';
                                                                }
                                                            }}
                                                        />
                                                    ) : null}
                                                    <div className={`h-6 w-6 rounded-full flex items-center justify-center ${fotoUrl ? 'hidden' : 'flex'}`}>
                                                        <User size={16} className="text-gray-500" />
                                                    </div>
                                                </div>
                                                <span className="text-gray-700">
                                                    {pacienteSelecionado?.nome_completo || (isLoadingPacientes ? 'Carregando...' : 'Selecione o paciente')}
                                                </span>
                                            </>
                                        );
                                    })()}
                                </div>
                                <ChevronDown size={16} className="text-gray-400" />
                            </button>
                        }
                        items={isLoadingPacientes ? 
                            [{ id: 'loading', label: 'Carregando...', disabled: true }] :
                            pacientes?.map((p) => ({
                                id: String(p.id),
                                label: p.nome_completo,
                                image: p.fotoURL ? getPacienteFotoUrl(p.fotoURL) : null,
                                imageAlt: p.nome_completo,
                                icon: User,
                                iconColor: 'text-gray-600',
                                onClick: () => {
                                    if (!isEditing) {
                                        setFormData(prev => ({
                                            ...prev,
                                            paciente_id: String(p.id)
                                        }));
                                    }
                                }
                            })) || []
                        }
                        position="bottom-left"
                        className="w-full"
                    />
                </div>

                {/* Tipo de Tarefa - Usando DropdownMenu */}
                <div>
                    <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                        <Activity size={16} className="text-gray-500" />
                        Tipo de Tarefa
                    </label>
                    <DropdownMenu
                        trigger={
                            <button
                                type="button"
                                onClick={(e) => e.preventDefault()}
                                className="w-full flex items-center justify-between gap-2 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 hover:bg-gray-50 transition"
                            >
                                <div className="flex items-center gap-2">
                                    {(() => {
                                        const tipoSelecionado = TIPO_TAREFAS.find(t => t.value === formData.tipo_tarefa);
                                        const Icon = tipoSelecionado?.icon || Activity;
                                        return (
                                            <>
                                                <Icon size={16} className="text-gray-500" />
                                                <span className="text-gray-700">
                                                    {tipoSelecionado?.label || "Selecione o tipo de tarefa"}
                                                </span>
                                            </>
                                        );
                                    })()}
                                </div>
                                <ChevronDown size={16} className="text-gray-400" />
                            </button>
                        }
                        items={TIPO_TAREFAS.map(t => ({
                            id: t.value,
                            label: t.label,
                            icon: t.icon,
                            iconColor: 'text-gray-600',
                            onClick: () => {
                                setFormData(prev => ({
                                    ...prev,
                                    tipo_tarefa: t.value
                                }));
                            }
                        }))}
                        position="bottom-left"
                        className="w-full"
                    />
                </div>

                {/* Horário agendado */}
                <div>
                    <label className="block text-gray-600 font-medium mb-1 flex items-center gap-1">
                        <Clock size={16} /> Horário Agendado (Data e Hora)
                    </label>
                    <input
                        type="datetime-local"
                        name="hora_agendada"
                        value={formData.hora_agendada}
                        onChange={handleChange}
                        required
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition"
                    />
                </div>

                {/* Descrição */}
                <div>
                    <label className="block text-gray-600 font-medium mb-1">
                        Descrição / Instruções
                    </label>
                    <textarea
                        name="descricao"
                        value={formData.descricao}
                        onChange={handleChange}
                        rows="3"
                        placeholder="Descreva a tarefa ou instruções específicas..."
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition"
                    ></textarea>
                </div>

                {/* Botões */}
                <div className="flex justify-end gap-3 pt-4">
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="flex items-center gap-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-lg transition"
                        disabled={isSubmitting}
                    >
                        <XCircle size={18} /> Cancelar
                    </button>

                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`flex items-center gap-2 font-semibold py-2 px-4 rounded-lg shadow-md transition disabled:opacity-50 ${isEditing ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}
                    >
                        {isSubmitting ? "Salvando..." : (isEditing ? <> <Save size={18} /> Salvar Edição</> : <> <Save size={18} /> Agendar Tarefa</>)}
                    </button>
                </div>
            </form>

            {/* 🛑 O ConfirmacaoModal FOI REMOVIDO DAQUI. O componente pai deve renderizá-lo como um irmão do Modal principal. */}
        </Modal>
    );
};

export default AgendaTarefaForm;